import { getAuth } from 'firebase/auth';
import { getApiUrl } from '../config/api';

const PENDING_UPGRADE_KEY = 'focuspips_pending_upgrade';
const PENDING_UPGRADE_TTL_MS = 10 * 60 * 1000;
const PENDING_UPGRADE_META_KEY = 'focuspips_pending_upgrade_meta';

export type PendingUpgradeMeta = { plan: string; billingInterval: string };

/** meta (plan/billingInterval) optionnelle : consommée au retour par checkout_completed (tracking.tsv). */
export function markPendingUpgrade(meta?: PendingUpgradeMeta): void {
  if (typeof sessionStorage === 'undefined') return;
  sessionStorage.setItem(PENDING_UPGRADE_KEY, String(Date.now()));
  if (meta) {
    sessionStorage.setItem(PENDING_UPGRADE_META_KEY, JSON.stringify(meta));
  }
}

/** Lit puis supprime la meta stockée par markPendingUpgrade (usage unique, au retour Stripe). */
export function consumePendingUpgradeMeta(): PendingUpgradeMeta | null {
  if (typeof sessionStorage === 'undefined') return null;
  const raw = sessionStorage.getItem(PENDING_UPGRADE_META_KEY);
  sessionStorage.removeItem(PENDING_UPGRADE_META_KEY);
  if (!raw) return null;
  try {
    return JSON.parse(raw) as PendingUpgradeMeta;
  } catch {
    return null;
  }
}

export function hasPendingUpgrade(): boolean {
  if (typeof sessionStorage === 'undefined') return false;
  const raw = sessionStorage.getItem(PENDING_UPGRADE_KEY);
  if (!raw) return false;
  const startedAt = Number(raw);
  if (!Number.isFinite(startedAt) || Date.now() - startedAt > PENDING_UPGRADE_TTL_MS) {
    sessionStorage.removeItem(PENDING_UPGRADE_KEY);
    return false;
  }
  return true;
}

export function clearPendingUpgrade(): void {
  if (typeof sessionStorage === 'undefined') return;
  sessionStorage.removeItem(PENDING_UPGRADE_KEY);
  // Une meta orpheline (polling expiré sans upgrade) ne doit pas être attribuée au checkout suivant.
  sessionStorage.removeItem(PENDING_UPGRADE_META_KEY);
}

/** Stripe success_url can append ?payment=success — treat it like a pending upgrade. */
export function consumePaymentSuccessUrlParam(): boolean {
  if (typeof window === 'undefined') return false;
  const params = new URLSearchParams(window.location.search);
  if (params.get('payment') !== 'success' && params.get('upgraded') !== '1') {
    return false;
  }
  markPendingUpgrade();
  params.delete('payment');
  params.delete('upgraded');
  const query = params.toString();
  const nextUrl = `${window.location.pathname}${query ? `?${query}` : ''}${window.location.hash}`;
  window.history.replaceState({}, '', nextUrl);
  return true;
}

export function isPaidPremiumState(isPremium: boolean, isTrial: boolean, subscriptionType: string): boolean {
  if (!isPremium || isTrial) return false;
  const sub = subscriptionType.toLowerCase();
  return sub.includes('premium') || sub.includes('lifetime');
}

export type SubscriptionSsePayload = {
  type?: string;
  isPremium?: boolean;
  isTrial?: boolean;
  subscriptionType?: string;
  isLifetime?: boolean;
};

function connectSubscriptionUpgradeSse(
  token: string,
  onUpdated: (payload: SubscriptionSsePayload) => void,
  signal: AbortSignal,
): Promise<'updated' | 'closed' | 'auth_error' | 'network_error'> {
  return new Promise((resolve) => {
    const xhr = new XMLHttpRequest();
    const requestUrl = `${getApiUrl('/api/subscription/events')}?access_token=${encodeURIComponent(token)}`;
    let processedLength = 0;
    let sseBuffer = '';
    let settled = false;

    const finish = (result: 'updated' | 'closed' | 'auth_error' | 'network_error') => {
      if (settled) return;
      settled = true;
      xhr.abort();
      resolve(result);
    };

    const onAbort = () => finish('closed');
    signal.addEventListener('abort', onAbort);

    xhr.open('GET', requestUrl, true);
    xhr.setRequestHeader('Authorization', `Bearer ${token}`);
    xhr.setRequestHeader('Accept', 'text/event-stream');

    xhr.onprogress = () => {
      const newText = xhr.responseText.slice(processedLength);
      processedLength = xhr.responseText.length;
      if (!newText) return;

      sseBuffer += newText.replace(/\r\n/g, '\n');
      const events = sseBuffer.split('\n\n');
      sseBuffer = events.pop() ?? '';

      for (const frame of events) {
        const dataLine = frame.split('\n').find((line) => line.startsWith('data: '));
        if (!dataLine) continue;

        try {
          const payload = JSON.parse(dataLine.slice(6)) as SubscriptionSsePayload;
          if (payload.type === 'subscription_updated') {
            onUpdated(payload);
            finish('updated');
            return;
          }
        } catch {
          // ignore malformed frames
        }
      }
    };

    xhr.onload = () => {
      signal.removeEventListener('abort', onAbort);
      if (xhr.status === 401 || xhr.status === 403) {
        finish('auth_error');
        return;
      }
      if (xhr.status >= 400) {
        finish('network_error');
        return;
      }
      finish('closed');
    };

    xhr.onerror = () => {
      signal.removeEventListener('abort', onAbort);
      finish('network_error');
    };

    xhr.onabort = () => {
      signal.removeEventListener('abort', onAbort);
      if (!settled) finish('closed');
    };

    xhr.send();
  });
}

/**
 * SSE push depuis /api/payment — une connexion HTTP idle, pas de listener Firestore.
 * Reconnecte automatiquement tant que l'utilisateur est connecté.
 */
export function startSubscriptionUpgradeListener(
  onUpdated: (payload: SubscriptionSsePayload) => void,
): () => void {
  const abortController = new AbortController();
  let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  let reconnectDelayMs = 3000;

  const scheduleReconnect = () => {
    if (abortController.signal.aborted) return;
    reconnectTimer = setTimeout(() => {
      void connect();
    }, reconnectDelayMs);
    reconnectDelayMs = Math.min(reconnectDelayMs * 2, 30000);
  };

  const connect = async () => {
    if (abortController.signal.aborted) return;

    const currentUser = getAuth().currentUser;
    if (!currentUser) {
      scheduleReconnect();
      return;
    }

    let token: string;
    try {
      token = await currentUser.getIdToken();
    } catch {
      scheduleReconnect();
      return;
    }

    if (!token) {
      scheduleReconnect();
      return;
    }

    const result = await connectSubscriptionUpgradeSse(
      token,
      onUpdated,
      abortController.signal,
    );

    if (abortController.signal.aborted || result === 'updated') {
      if (result === 'updated' && !abortController.signal.aborted) {
        reconnectDelayMs = 3000;
        scheduleReconnect();
      }
      return;
    }

    if (result === 'auth_error') {
      return;
    }

    scheduleReconnect();
  };

  void connect();

  return () => {
    abortController.abort();
    if (reconnectTimer) clearTimeout(reconnectTimer);
  };
}
